import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { LEAD_SOURCES, LEAD_COUNTRIES, AD_GROUP_LABELS } from './leadOptions';
import LeadSelect from './LeadSelect';
import LeadLocationPicker from './LeadLocationPicker';
import { formatPhone, waHref } from './phoneFormat';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

/**
 * One lead, in full: the record, its follow-up log, and every other time this
 * number has been in touch.
 *
 * The history panel is the thing the spreadsheet could never do. 51 numbers in
 * the sheet appeared more than once and nothing joined them up, so the office
 * re-pitched people it had already spoken to and the conversion rate counted
 * the same prospect several times.
 */
/**
 * The WhatsApp glyph, inline.
 *
 * The project only ships Font Awesome's SOLID set — there is no brands package
 * — and pulling one in for a single mark is not worth a dependency.
 */
const WhatsAppIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
        <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z" />
        <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.9 9.9 0 0 0 4.76 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm0 18.02h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.05-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24a8.2 8.2 0 0 1 5.83 2.42 8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.24-8.24 8.24z" />
    </svg>
);

export default function LeadDrawer({ leadId, meta, staff, areas, onClose, onSaved, canDelete }) {
    const [lead, setLead] = useState(null);
    const [form, setForm] = useState(null);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [clients, setClients] = useState([]);
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [flash, setFlash] = useState('');
    /* Sales commission is office-and-above only. The server enforces both
       halves too — supervisors may set the salesperson, only managers may
       record a payout — so this decides what is OFFERED, not what is allowed. */
    const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
    const canSeeSales = userRole === 'manager' || userRole === 'supervisor';
    const canPayCommission = userRole === 'manager';
    const [commissionDate, setCommissionDate] = useState('');
    /* Salespeople are their OWN short list, not the staff roster — sales is a
       role a couple of people hold, and one of them need not be a portal user
       at all. Only Rheem for now. */
    const [salesPeople, setSalesPeople] = useState([]);

    // Loaded once per drawer. Kept up here with the other hooks: further down
    // it sat after an early return and broke the rules-of-hooks order.
    useEffect(() => {
        if (!canSeeSales) return undefined;
        let dead = false;
        axios.get('/api/leads/sales-persons')
            .then(({ data }) => { if (!dead) setSalesPeople(data.sales_persons || []); })
            .catch(() => { if (!dead) setSalesPeople([]); });
        return () => { dead = true; };
    }, [canSeeSales]);

    /**
     * The record AND the form, from one lead.
     *
     * Every action that writes server-side has to come back through here. It
     * did not before, and the cost was worse than a stale label: pressing
     * "Followed up" advanced the status in the database while the drawer went
     * on showing the old one, so the next Save wrote the old status straight
     * back over it.
     */
    const applyLead = React.useCallback((l) => {
        setLead(l);
        setForm({
            inquiry_date: l.inquiry_date ? String(l.inquiry_date).slice(0, 10) : '',
            name: l.name || '',
            phone: l.phone || '',
            email: l.email || '',
            lead_type: l.lead_type || 'holiday_home_company',
            source: l.source || '',
            ad_ref: l.ad_ref || '',
            area: l.area || '',
            units_count: l.units_count ?? '',
            status: l.status || 'new',
            lost_reason: l.lost_reason || '',
            lost_note: l.lost_note || '',
            next_follow_up_on: l.next_follow_up_on ? String(l.next_follow_up_on).slice(0, 10) : '',
            notes: l.notes || '',
            starred: !!l.starred,
            assigned_to: l.assigned_to ?? '',
            followed_up_by: l.followed_up_by ?? '',
            latitude: l.latitude ?? '',
            longitude: l.longitude ?? '',
            place_name: l.place_name || '',
            maps_url: l.maps_url || '',
        });
    }, []);

    const load = React.useCallback(async () => {
        try {
            const { data } = await axios.get(`/api/leads/${leadId}`);
            applyLead(data.lead);
            setError('');
        } catch (e) {
            setError(e.response?.data?.error || 'Could not load this lead.');
        }
    }, [leadId, applyLead]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (!lead || !form) {
        // The shape of the thing that is coming, rather than the word "Loading"
        // on an empty white panel — the drawer is tall and a bare line of text
        // in the corner of it read as a broken screen.
        return (
            <div className="lds__drawerOverlay" onClick={onClose}>
                <div className="lds__drawer" onClick={(e) => e.stopPropagation()}>
                    <div className="lds__drawerHead">
                        <div style={{ flex: 1 }}>
                            <span className="lds__skel lds__skel--title" />
                            <span className="lds__skel lds__skel--sub" />
                        </div>
                        <button className="lds__drawerClose" onClick={onClose} aria-label="Close">×</button>
                    </div>
                    <div className="lds__drawerBody">
                        {error ? <div className="lds__err">{error}</div> : (
                            <div className="lds__skelBody" aria-label="Loading the lead" aria-busy="true">
                                <span className="lds__skel lds__skel--actions" />
                                <div className="lds__skelGrid">
                                    <span className="lds__skel lds__skel--field" />
                                    <span className="lds__skel lds__skel--field" />
                                </div>
                                <span className="lds__skel lds__skel--field" />
                                <div className="lds__skelGrid">
                                    <span className="lds__skel lds__skel--field" />
                                    <span className="lds__skel lds__skel--field" />
                                </div>
                                <div className="lds__skelGrid">
                                    <span className="lds__skel lds__skel--field" />
                                    <span className="lds__skel lds__skel--field" />
                                </div>
                                <span className="lds__skel lds__skel--field" />
                                <span className="lds__skel lds__skel--notes" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    // A GADS-… ref means the ad brought them: the source follows, unless the
    // office has already recorded something more specific than WhatsApp.
    const setAdRef = (v) => setForm((f) => ({
        ...f,
        ad_ref: v,
        source: /GADS-[A-Z0-9]{5}/i.test(v) && (!f.source || f.source === 'WhatsApp') ? 'Google Ads' : f.source,
    }));
    const adMeta = lead?.ad_meta || null;
    const staffOptions = (staff || []).map((u) => ({
        value: u.id,
        label: [u.first_name, u.last_name].filter(Boolean).join(' '),
    }));

    const label = (group, key) => meta?.labels?.[group]?.[key] || key;
    // A plain YYYY-MM-DD is a CALENDAR DATE, not an instant: it is rendered as
    // typed. Anything carrying a time is a real timestamp and is read in Dubai.
    // Running a bare date through a zone is how a row lands a day early.
    const d = (v, fmt = 'd LLL yyyy') => {
        if (!v) return '';
        const s = String(v);
        const dt = /^\d{4}-\d{2}-\d{2}$/.test(s)
            ? DateTime.fromISO(s)
            : DateTime.fromISO(s, { zone: 'utc' }).setZone('Asia/Dubai');
        return dt.toFormat(fmt);
    };

    const save = async () => {
        setBusy(true); setError(''); setFlash('');
        try {
            const body = { ...form };
            // A lead that is no longer lost should not keep carrying why it was.
            if (body.status !== 'lost') { body.lost_reason = ''; body.lost_note = ''; }
            const { data } = await axios.put(`/api/leads/${leadId}`, body);
            applyLead(data.lead);
            setFlash('Saved.');
            onSaved?.();
        } catch (e) {
            setError(e.response?.data?.error || 'Could not save this lead.');
        } finally { setBusy(false); }
    };

    /* Both write through applyLead like every other action in this drawer, so
       the panel reflects the server rather than what we hoped it did — the same
       reason "Followed up" was made to round-trip. */
    const saveSalesPerson = async (value) => {
        setBusy(true); setError('');
        try {
            const { data } = await axios.put(`/api/leads/${leadId}/sales-person`,
                { sales_person_id: value === '' ? null : value });
            applyLead(data.lead);
            onSaved?.();
        } catch (e) {
            setError(e.response?.data?.error || 'Could not set the salesperson.');
        } finally { setBusy(false); }
    };

    const saveCommission = async (paidOn) => {
        setBusy(true); setError('');
        try {
            const { data } = await axios.put(`/api/leads/${leadId}/commission`, { paid_on: paidOn });
            applyLead(data.lead);
            setCommissionDate('');
            setFlash(paidOn ? 'Commission recorded.' : 'Commission cleared.');
            onSaved?.();
        } catch (e) {
            setError(e.response?.data?.error || 'Could not record the commission.');
        } finally { setBusy(false); }
    };

    const addNote = async () => {
        if (!note.trim()) return;
        setBusy(true); setError('');
        try {
            const { data } = await axios.post(`/api/leads/${leadId}/notes`, { note });
            applyLead(data.lead);
            setNote('');
            onSaved?.();
        } catch (e) {
            setError(e.response?.data?.error || 'Could not add the note.');
        } finally { setBusy(false); }
    };

    // Nobody has answered this person yet, so the tap is a REPLY, not a
    // follow-up — there is nothing to follow up, and the lead becomes
    // "Replied" rather than "Following up" (2026-08-30).
    const firstReply = lead?.status === 'new';

    // "I dealt with this one", in one tap: who, when, the log line, and the
    // chase nudge silenced. Typed out by hand, two of those three get skipped.
    const markFollowedUp = async () => {
        setBusy(true); setError(''); setFlash('');
        try {
            const was = lead.status;
            const { data } = await axios.post(`/api/leads/${leadId}/followed-up`,
                note.trim() ? { note } : {});
            applyLead(data.lead);
            setNote('');
            // Say what it did to the status, rather than leaving somebody to
            // wonder why the row looks the same. Chasing a lead that has
            // already been quoted does not walk it back down the funnel.
            const what = firstReply ? 'Reply logged' : 'Follow-up logged';
            setFlash(data.lead.status !== was
                ? `${what} — moved to ${label('status', data.lead.status)}.`
                : `${what} — still ${label('status', data.lead.status)}.`);
            onSaved?.();
        } catch (e) {
            setError(e.response?.data?.error
                || (firstReply ? 'Could not record the reply.' : 'Could not record the follow-up.'));
        } finally { setBusy(false); }
    };

    // Registering is the Add Client form, not a one-tap create: it opens a Zoho
    // contact and a billing address, and neither should happen from a button
    // press with nobody looking. The lead rides along in router state so the
    // new client is linked back to it on save.
    const registerClient = () => {
        navigate('/clients/new', {
            state: {
                fromLead: {
                    id: lead.id,
                    first_name: (lead.name || '').split(' ')[0] || '',
                    last_name: (lead.name || '').split(' ').slice(1).join(' '),
                    company_name: lead.lead_type === 'owner' ? '' : (lead.name || ''),
                    display_name: lead.name || '',
                    email: lead.email || '',
                    contact_number: lead.phone || '',
                    client_type: lead.lead_type === 'owner' ? 'individual' : 'business',
                },
            },
        });
    };

    const openConnect = async () => {
        setConnecting((v) => !v);
        setError('');
        if (clients.length) return;
        try {
            const { data } = await axios.get('/api/leads/clients');
            setClients(data.clients || []);
        } catch (e) {
            setError(e.response?.data?.error || 'Could not load the client list.');
        }
    };

    const connectTo = async (clientId) => {
        if (!clientId) return;
        setBusy(true); setError(''); setFlash('');
        try {
            const body = { client_id: clientId };
            // Connecting a lead to a client IS the registration, unless it has
            // already gone further than that.
            if (!['registered', 'booked'].includes(form.status)) body.status = 'registered';
            const { data } = await axios.put(`/api/leads/${leadId}`, body);
            applyLead(data.lead);
            setConnecting(false);
            setFlash(`Connected to ${data.lead.client_name}.`);
            onSaved?.();
        } catch (e) {
            setError(e.response?.data?.error || 'Could not connect this lead.');
        } finally { setBusy(false); }
    };


    const remove = async () => {
        if (!window.confirm('Delete this lead? It disappears from the pipeline and from every figure built on it.')) return;
        setBusy(true);
        try {
            await axios.delete(`/api/leads/${leadId}`);
            onSaved?.();
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || 'Could not delete this lead.');
            setBusy(false);
        }
    };

    const chatHref = waHref(lead.phone);

    return (
        <div className="lds__drawerOverlay" onClick={onClose}>
            <div className="lds__drawer" onClick={(e) => e.stopPropagation()}>
                <div className="lds__drawerHead">
                    <div>
                        <h3 className="lds__drawerTitle">
                            {lead.name || <span className="lds__anon">No name recorded</span>}
                        </h3>
                        <p className="lds__drawerSub">
                            Inquiry #{lead.id} · first in touch {d(lead.inquiry_date)}
                            {lead.created_by_name ? ` · logged by ${lead.created_by_name}` : ''}
                        </p>
                    </div>
                    <button className="lds__drawerClose" onClick={onClose} aria-label="Close">×</button>
                </div>

                {/* ACTIONS LIVE IN THE HEAD (2026-08-30). They used to be the
                    first thing in the scrolling body, so they left the screen
                    the moment you read anything about the lead — and they are
                    what you opened the drawer to press. The head does not
                    scroll, so they are always there. */}
                    <div className="lds__actions">
                        <button
                            className="lds__btn"
                            onClick={markFollowedUp}
                            disabled={busy}
                            title={firstReply
                                ? 'Records that you answered them, and marks the lead handled by you'
                                : 'Records that you chased them, and marks the lead handled by you'}
                        >
                            {firstReply ? 'Replied' : 'Followed up'}
                        </button>
                        {!lead.client_id && (
                            <button className="lds__btn" onClick={registerClient} disabled={busy}>
                                Register client
                            </button>
                        )}
                        {!lead.client_id && (
                            <button className={`lds__btn${connecting ? ' lds__btn--on' : ''}`}
                                onClick={openConnect} disabled={busy}>
                                Connect client
                            </button>
                        )}
                        {chatHref && (
                            <a className="lds__btn lds__btn--wa" href={chatHref}
                                target="_blank" rel="noreferrer"
                                title={`Open the chat with ${formatPhone(lead.phone)}`}
                                aria-label="Open WhatsApp">
                                <WhatsAppIcon />
                            </a>
                        )}
                    </div>

                <div className="lds__drawerBody">
                    {error && <div className="lds__err">{error}</div>}
                    {flash && <div className="lds__ok">{flash}</div>}

                    {connecting && (
                        <div className="lds__field">
                            <label>Which client did this become?</label>
                            <LeadSelect
                                options={clients.map((c) => ({
                                    value: c.id,
                                    label: c.is_active === false ? `${c.display_name} (archived)` : c.display_name,
                                }))}
                                value={null}
                                onChange={connectTo}
                                placeholder={clients.length ? 'Search clients…' : 'Loading…'}
                            />
                        </div>
                    )}

                    <div className="lds__grid2">
                        <div className="lds__field">
                            <label>Inquiry date</label>
                            <input type="date" value={form.inquiry_date}
                                onChange={(e) => set('inquiry_date', e.target.value)} />
                        </div>
                        <div className="lds__field">
                            <label>Status</label>
                            <LeadSelect
                                options={(meta?.statuses || []).map((x) => ({ value: x, label: label('status', x) }))}
                                value={form.status}
                                onChange={(v) => set('status', v || 'new')}
                            />
                        </div>
                    </div>

                    {/* Directly under Status: the moment somebody marks a lead Not
                        interested, why is the next question — and it is the one
                        the spreadsheet failed to answer 22 times out of 93. */}
                    {form.status === 'lost' && (
                        <>
                            <div className="lds__field">
                                <label>Why not interested?</label>
                                <LeadSelect
                                    options={(meta?.lost_reasons || []).map((x) => ({ value: x, label: label('lost_reason', x) }))}
                                    value={form.lost_reason}
                                    onChange={(v) => set('lost_reason', v)}
                                    placeholder="Required"
                                    isClearable
                                />
                            </div>
                            <div className="lds__field">
                                <label>In their words</label>
                                <input value={form.lost_note} onChange={(e) => set('lost_note', e.target.value)}
                                    placeholder="What they actually said" />
                            </div>
                            {form.lost_reason === 'area_not_covered' && (
                                <div className="lds__field">
                                    <label>Where is the property?</label>
                                    <LeadLocationPicker
                                        value={form}
                                        onChange={(loc) => setForm((f) => ({ ...f, ...loc }))}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {/* THE CUSTOMER's name. Our own people are the two pickers
                        further down — "Name" on its own read as either. */}
                    <div className="lds__field">
                        <label>Customer name</label>
                        <input value={form.name} onChange={(e) => set('name', e.target.value)}
                            placeholder="The name of the person or company who got in touch" />
                    </div>

                    <div className="lds__grid2">
                        <div className="lds__field">
                            <label>Phone</label>
                            {/* The real phone input, not a text box: it keeps the
                                number dialable and in one shape, which is what
                                makes the chat link and the client match work. */}
                            <PhoneInput
                                defaultCountry="ae"
                                countries={LEAD_COUNTRIES}
                                value={form.phone}
                                onChange={(v) => set('phone', v)}
                                className="client-phone-wrapper"
                                inputClassName="client-phone-input"
                            />
                        </div>
                        <div className="lds__field">
                            <label>Email</label>
                            <input value={form.email} onChange={(e) => set('email', e.target.value)} />
                        </div>
                    </div>

                    <div className="lds__grid2">
                        <div className="lds__field">
                            <label>Units</label>
                            <input type="number" min="0" value={form.units_count}
                                onChange={(e) => set('units_count', e.target.value)} />
                        </div>
                        <div className="lds__field">
                            <label>Where they came from</label>
                            <LeadSelect
                                options={LEAD_SOURCES.map((x) => ({ value: x, label: x }))}
                                value={form.source}
                                onChange={(v) => set('source', v)}
                                placeholder="Not recorded"
                                isClearable
                            />
                        </div>
                    </div>

                    <div className="lds__grid2">
                        <div className="lds__field">
                            <label>Ad ref</label>
                            <input value={form.ad_ref} placeholder="GADS-XXXXX"
                                onChange={(e) => setAdRef(e.target.value)} />
                            <p className="lds__fieldNote">
                                From their WhatsApp message, e.g. "(ref GADS-7K2QD)".
                            </p>
                        </div>
                        {adMeta && (
                            <div className="lds__field">
                                <label>From the ad</label>
                                <p className="lds__adMeta">
                                    {[AD_GROUP_LABELS[adMeta.utm_content] || adMeta.utm_content,
                                        adMeta.utm_campaign,
                                        adMeta.landing && adMeta.landing !== '/' ? `landed on ${adMeta.landing}` : null]
                                        .filter(Boolean).join(' · ') || 'Google ad'}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="lds__grid2">
                        <div className="lds__field">
                            <label>Area</label>
                            <LeadSelect
                                creatable
                                options={(areas || []).map((a) => ({ value: a, label: a }))}
                                value={form.area}
                                onChange={(v) => set('area', v)}
                                placeholder="Pick or type a new one"
                                isClearable
                            />
                        </div>
                        <div className="lds__field">
                            <label>Type</label>
                            <LeadSelect
                                options={(meta?.lead_types || []).map((x) => ({ value: x, label: label('lead_type', x) }))}
                                value={form.lead_type}
                                onChange={(v) => set('lead_type', v || 'holiday_home_company')}
                            />
                        </div>
                    </div>

                    <div className="lds__field">
                        <label>Follow up on</label>
                        <input type="date" value={form.next_follow_up_on}
                            onChange={(e) => set('next_follow_up_on', e.target.value)} />
                    </div>

                    {/* Ours, not theirs. A lead with a follow-up date and no
                        owner is still nobody's job. */}
                    <div className="lds__grid2">
                        <div className="lds__field">
                            <label>Handled by</label>
                            <LeadSelect
                                options={staffOptions}
                                value={form.assigned_to}
                                onChange={(v) => set('assigned_to', v)}
                                placeholder="Nobody yet"
                                isClearable
                            />
                        </div>
                        <div className="lds__field">
                            <label>Last contact by</label>
                            <LeadSelect
                                options={staffOptions}
                                value={form.followed_up_by}
                                onChange={(v) => set('followed_up_by', v)}
                                placeholder="Nobody yet"
                                isClearable
                            />
                            {lead.followed_up_at && (
                                <p className="lds__fieldNote">
                                    Last contact {d(lead.followed_up_at, 'd LLL yyyy, HH:mm')}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="lds__field">
                        <label>Notes</label>
                        <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} />
                    </div>

                    <div className="lds__field">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'none', letterSpacing: 0, fontSize: 13.5, color: '#111827' }}>
                            <input type="checkbox" checked={form.starred}
                                onChange={(e) => set('starred', e.target.checked)} />
                            {/* Named for the ⭐ it puts in the list, so the two are
                                obviously the same thing. */}
                            Starred
                        </label>
                    </div>

                    <div className="lds__sectionTitle">Client</div>
                    {lead.client_id ? (
                        <p style={{ margin: '0 0 10px', fontSize: 13.5 }}>
                            Became{' '}
                            <a className="lds__link" href={`/clients/${lead.client_id}`}>{lead.client_name}</a>
                            {lead.converted_at ? ` · ${d(lead.converted_at)}` : ''}
                            {lead.client_active === false ? ' · archived' : ''}
                        </p>
                    ) : (
                        <p style={{ margin: '0 0 10px', fontSize: 13.5, color: '#6b7280' }}>
                            Not linked to a client yet.
                        </p>
                    )}
                    <div className="lds__sectionTitle">Follow-up log</div>
                    <div className="lds__field">
                        <textarea value={note} onChange={(e) => setNote(e.target.value)}
                            placeholder="What happened when you contacted them" style={{ minHeight: 62 }} />
                        <div style={{ marginTop: 8 }}>
                            <button className="lds__btn lds__btn--primary" onClick={addNote}
                                disabled={busy || !note.trim()}>Add note</button>
                        </div>
                    </div>
                    {(lead.notes || []).length === 0 && (
                        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Nothing logged yet.</p>
                    )}
                    {(lead.notes || []).map((n) => (
                        <div key={n.id} className="lds__noteRow">
                            <div className="lds__noteMeta">
                                {d(n.created_at, 'd LLL yyyy, HH:mm')}
                                {n.created_by_name ? ` · ${n.created_by_name}` : ''}
                            </div>
                            <div className="lds__noteText">{n.note}</div>
                        </div>
                    ))}

                    {(lead.history || []).length > 0 && (
                        <>
                            <div className="lds__sectionTitle">
                                This number has been in touch {lead.history.length + 1} times
                            </div>
                            {lead.history.map((h) => (
                                <div key={h.id} className="lds__histRow">
                                    <span>{d(h.inquiry_date)} · {h.name || <span className="lds__anon">no name</span>}</span>
                                    <span className={`lds__chip lds__chip--${h.status}`}>{label('status', h.status)}</span>
                                </div>
                            ))}
                        </>
                    )}

                    {canSeeSales && (
                        <>
                            <div className="lds__sectionTitle">Sales</div>
                            <div className="lds__salesBox">
                                <div className="lds__salesRow">
                                    <label htmlFor="lds-sales-person">Salesperson</label>
                                    <div className="lds__salesField">
                                        <LeadSelect
                                            inputId="lds-sales-person"
                                            value={form.sales_person_id ?? lead.sales_person_id ?? ''}
                                            onChange={(v) => saveSalesPerson(v)}
                                            options={salesPeople.map((m) => ({
                                                value: String(m.id), label: m.name,
                                            }))}
                                            /* While there is exactly one salesperson there is
                                               nothing to choose, so the field shows her rather than
                                               an empty "Nobody yet" that reads like data is missing.
                                               Falls back to a real placeholder once there are
                                               several to pick between. */
                                            placeholder={salesPeople.length === 1 ? salesPeople[0].name : 'Choose a salesperson'}
                                            isDisabled={busy}
                                        />
                                    </div>
                                </div>

                                {/* The commission condition, stated plainly rather than as a
                                    number the reader has to interpret. */}
                                <div className="lds__salesRow">
                                    <label>Paid bookings</label>
                                    <div className="lds__salesField">
                                        {lead.client_id ? (
                                            <span className={`lds__qualChip${(lead.paid_bookings_count || 0) >= 2 ? ' is-met' : ''}`}>
                                                {lead.paid_bookings_count || 0} paid
                                                {(lead.paid_bookings_count || 0) >= 2
                                                    ? ' · commission qualifies'
                                                    : ` · needs ${2 - (lead.paid_bookings_count || 0)} more`}
                                            </span>
                                        ) : (
                                            <span className="lds__qualChip">Not converted to a client yet</span>
                                        )}
                                    </div>
                                </div>

                                <div className="lds__salesRow">
                                    <label>Commission</label>
                                    <div className="lds__salesField">
                                        {lead.commission_paid_at ? (
                                            <div className="lds__commPaid">
                                                Paid {d(lead.commission_paid_at, 'd LLL yyyy')}
                                                {lead.commission_paid_by_name ? ` · recorded by ${lead.commission_paid_by_name}` : ''}
                                                {canPayCommission && (
                                                    <button className="lds__btn lds__btn--ghost" disabled={busy}
                                                        onClick={() => saveCommission(null)}>Undo</button>
                                                )}
                                            </div>
                                        ) : canPayCommission ? (
                                            <div className="lds__commPay">
                                                <input type="date" value={commissionDate} disabled={busy}
                                                    onChange={(e) => setCommissionDate(e.target.value)}
                                                    aria-label="Date the commission was paid" />
                                                <button className="lds__btn lds__btn--primary" disabled={busy || !commissionDate}
                                                    onClick={() => saveCommission(commissionDate)}>Mark paid</button>
                                            </div>
                                        ) : (
                                            <span className="lds__qualChip">Not paid yet</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {canDelete && (
                        <>
                            <div className="lds__sectionTitle">Remove this lead</div>
                            <p style={{ margin: '0 0 10px', fontSize: 12.5, color: '#6b7280' }}>
                                It disappears from the pipeline and from every figure built on it.
                            </p>
                            <button className="lds__btn lds__btn--danger" onClick={remove} disabled={busy}>
                                Delete inquiry #{lead.id}
                            </button>
                        </>
                    )}
                </div>

                {/* Save is the only thing that follows you down the panel. Closing
                    is the × in the header, and deleting is at the far end of the
                    page where you have to mean it. */}
                <div className="lds__drawerFoot">
                    <button className="lds__btn lds__btn--primary" onClick={save} disabled={busy}>
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}
